/* eslint-disable class-methods-use-this */
import axios from 'axios';
import authHeader from '@/services/auth-header';

const API_URL = `${process.env.VUE_APP_API_URL}`;

class SuperAdminStatsService {
  /**
   * GET /stats/superadmin/summary
   * Snapshot global del sistema.
   */
  getSummary() {
    return axios
      .get(`${API_URL}stats/superadmin/summary`, { headers: authHeader() })
      .then((response) => response.data);
  }

  /**
   * GET /stats/superadmin/workersByWorkCenter
   * Trabajadores agrupados por centro de trabajo.
   * Devuelve: [ [id, name, count], ... ]
   */
  getWorkersByWorkCenter() {
    return axios
      .get(`${API_URL}stats/superadmin/workersByWorkCenter`, { headers: authHeader() })
      .then((response) => response.data.map(([workCenterId, workCenterName, workers]) => ({
        workCenterId,
        workCenterName,
        workers,
      })));
  }

  /**
   * GET /stats/superadmin/clockEntriesByWorkCenter
   * Fichajes por centro de trabajo en un rango de fechas.
   * @param {string} dateStart - ISO 8601 (e.g. "2026-01-01T00:00:00")
   * @param {string} dateEnd   - ISO 8601 (e.g. "2026-01-31T23:59:59")
   * Devuelve: [ [id, name, count], ... ]
   */
  getClockEntriesByWorkCenter(dateStart, dateEnd) {
    return axios
      .get(`${API_URL}stats/superadmin/clockEntriesByWorkCenter`, {
        params: { dateStart, dateEnd },
        headers: authHeader(),
      })
      .then((response) => response.data.map(([workCenterId, workCenterName, entries]) => ({
        workCenterId,
        workCenterName,
        entries,
      })));
  }

  /**
   * GET /stats/superadmin/manualClockEntriesByWorkCenter
   * Fichajes manuales y de administrador por centro en un rango de fechas.
   * @param {string} dateStart
   * @param {string} dateEnd
   * Devuelve: [ [id, name, manual, admin], ... ]
   */
  getManualClockEntriesByWorkCenter(dateStart, dateEnd) {
    return axios
      .get(`${API_URL}stats/superadmin/manualClockEntriesByWorkCenter`, {
        params: { dateStart, dateEnd },
        headers: authHeader(),
      })
      .then((response) => response.data.map(([workCenterId, workCenterName, manual, admin]) => ({
        workCenterId,
        workCenterName,
        manual,
        admin,
      })));
  }

  /**
   * GET /stats/superadmin/incidencesByWorkCenter
   * Incidencias resueltas y sin resolver por centro (histórico completo).
   * Devuelve: [ [id, name, unsolved, solved], ... ]
   */
  getIncidencesByWorkCenter() {
    return axios
      .get(`${API_URL}stats/superadmin/incidencesByWorkCenter`, { headers: authHeader() })
      .then((response) => response.data.map(([workCenterId, workCenterName, unsolved, solved]) => ({
        workCenterId,
        workCenterName,
        unsolved,
        solved,
      })));
  }

  /**
   * GET /stats/superadmin/n3ByWorkCenter
   * Importe total y recuento de entradas N3 por centro para un año.
   * @param {number} year - (opcional, 0 = año actual)
   * Devuelve: [ [id, name, amount, count], ... ]
   */
  getN3ByWorkCenter(year = 0) {
    return axios
      .get(`${API_URL}stats/superadmin/n3ByWorkCenter`, {
        params: year ? { year } : {},
        headers: authHeader(),
      })
      .then((response) => response.data.map(([workCenterId, workCenterName, amount, count]) => ({
        workCenterId,
        workCenterName,
        amount,
        count,
      })));
  }

  /**
   * GET /stats/superadmin/n3MonthlyByWorkCenter
   * Detalle mensual de N3 por centro.
   * @param {number} year - (opcional, 0 = año actual)
   * Devuelve: [ [id, name, month, count, amount], ... ]
   */
  getN3MonthlyByWorkCenter(year = 0) {
    return axios
      .get(`${API_URL}stats/superadmin/n3MonthlyByWorkCenter`, {
        params: year ? { year } : {},
        headers: authHeader(),
      })
      .then((response) => response.data.map(([workCenterId, workCenterName, month, count, amount]) => ({
        workCenterId,
        workCenterName,
        month,
        count,
        amount,
      })));
  }

  /**
   * GET /stats/superadmin/clockEntriesMonthly
   * Recuento mensual de fichajes por centro para un año.
   * @param {number} year - (opcional, 0 = año actual)
   * Devuelve: [ [id, name, month, count], ... ]
   */
  getClockEntriesMonthly(year = 0) {
    return axios
      .get(`${API_URL}stats/superadmin/clockEntriesMonthly`, {
        params: year ? { year } : {},
        headers: authHeader(),
      })
      .then((response) => response.data.map(([workCenterId, workCenterName, month, count]) => ({
        workCenterId,
        workCenterName,
        month,
        count,
      })));
  }
}

export default new SuperAdminStatsService();
